import { Icon } from "@chakra-ui/react";
import { MdBarChart } from "react-icons/md";
import MiniStatistics from "../../../components/card/MiniStatistics";
import IconBox from "../../../components/icons/IconBox";
import { translate } from "../../../i18n";
import { useSelectedLocation } from "../../location-management/hooks/useSelectedLocation";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import { getDemoEventCount, getDemoGrowth, isDemoOrganization } from "../demoAnalyticsData";
import { AnalyticsFilterParams } from "../hooks/analyticsFilterParams";
import { useGetEventCount } from "../hooks/useGetEventCount";
import { computeGrowth } from "../utils/computeGrowth";
import { UNKNOWN_STAT_VALUE, isUnreadable, statPlaceholder } from "../utils/statValue";

type Props = {
    boxColor: string;
    brandColor: string;
    startDate: Date;
    endDate: Date;
    previousStartDate: Date | null;
    previousEndDate: Date | null;
    growthLabel?: string;
    filters?: AnalyticsFilterParams;
};
export default function EventCountCard({ boxColor, brandColor, startDate, endDate, previousStartDate, previousEndDate, growthLabel, filters }: Props) {
    const { selectedLocation } = useSelectedLocation();
    const { selectedOrganization } = useSelectedOrganization();
    const isDemo = isDemoOrganization(selectedOrganization?.id);
    /**
     * Every `analytics/*` route carries `@UseGuards(AdminAuthGuard)` — read from
     * `api/src/analytics/analytics.controller.ts` this run, not inherited — so a
     * stale dashboard token or an operator who is no longer an admin of this org
     * is REFUSED, with no outage at all, on top of the ordinary 5xx/network
     * paths. All of them leave `data === undefined`, which this card used to
     * render as a real number.
     */
    const { data: totalData, isInitialLoading: isTotalPending, isError: totalError } = useGetEventCount(selectedLocation?.id, undefined, undefined, filters);
    const { data, isInitialLoading: isPeriodPending, isError: periodError } = useGetEventCount(selectedLocation?.id, startDate, endDate, filters);
    // Comparison read: feeds `computeGrowth` only, which returns undefined when
    // either side is undefined, so a failed read removes the growth chip rather
    // than mis-stating it. Allowlisted in `analytics-read-error-gate`.
    const { data: previousData } = useGetEventCount(
        selectedLocation?.id,
        previousStartDate ?? undefined,
        previousEndDate ?? undefined,
        filters,
    );

    const orgId = selectedOrganization?.id;
    const totalUnreadable = isUnreadable(totalError, totalData);
    const periodUnreadable = isUnreadable(periodError, data);
    const demoTotal = isDemo ? getDemoEventCount(undefined, undefined, orgId) : 0;
    const demoPeriod = isDemo ? getDemoEventCount(startDate, endDate, orgId) : 0;
    // See `statPlaceholder`: the literal `'0'` that used to sit here was shown
    // for a read that was never issued (no location selected → a DISABLED query
    // → nothing, forever). A genuinely empty period still prints `0`, through
    // the `Boolean(...)` arm, because the API answers `{ value: 0 }`.
    const totalValue = isDemo ? demoTotal : totalUnreadable ? UNKNOWN_STAT_VALUE : Boolean(totalData) ? totalData?.value : statPlaceholder(isTotalPending);
    const partialValue = isDemo ? demoPeriod : periodUnreadable ? UNKNOWN_STAT_VALUE : Boolean(data) ? data?.value : statPlaceholder(isPeriodPending);
    const growth = isDemo
        ? getDemoGrowth(demoPeriod, previousStartDate, previousEndDate, getDemoEventCount, orgId)
        : previousStartDate ? computeGrowth(data?.value, previousData?.value) : undefined;

    return (
        <MiniStatistics
            startContent={
                <IconBox
                    w='56px'
                    h='56px'
                    bg={boxColor}
                    icon={<Icon w='32px' h='32px' as={MdBarChart} color={brandColor} />}
                />
            }
            name={translate('tabs.events')}
            value={`${partialValue} / ${totalValue}`}
            isError={!isDemo && (periodUnreadable || totalUnreadable)}
            growth={growth}
            growthLabel={growthLabel}
        />)
}
